import { useCallback, useEffect, useRef } from 'react';

import { getWindowSafe } from '@change-corgi/core/window';

/**
 * `sendToParent` (below) is never actually called anywhere in Corgi today — this app only listens
 * for messages coming up from the child; nothing here posts back down to the iframe. Even if it
 * were called, `window.parent` from Corgi's own page context is Corgi's *own* embedding parent (if
 * any), not the iframe — so this value has no effect on anything Corgi itself does.
 *
 * `sendToParent`/this origin really belong on the CHILD (iframe) app's side, which is a separate
 * codebase. There, `window.parent` correctly refers to this Corgi window, and
 * posting back requires knowing which real Corgi origin to target. When that's implemented there,
 * do NOT reuse this dummy value — resolve the actual origin instead, e.g. by reading an
 * `environment` query param that Corgi adds to the iframe's `src` and mapping it to the known
 * Corgi origins:
 *   - production -> https://www.change.org
 *   - staging -> https://www.staging-change.org
 *   - demo envs (jade, teal, ...) -> https://www.<env>.changeeng.org
 *   - PR previews -> https://corgi-<pr>.preview.changeeng.org
 *   - local dev -> whatever host the child is proxied against locally
 */
export const PARENT_ORIGIN = 'DUMMY_PARENT_ORIGIN';

type IncomingMessage<PAYLOAD> = { type: string; payload: PAYLOAD };
type OutgoingMessage = { type: string; payload?: unknown };

/**
 * Two-way `window.postMessage` helper for the host (Corgi) side of the sandbox.
 *
 * - Listens for a single `type` from `childOrigin` and calls `handler` with its
 *   payload. Messages of any other type or origin are ignored.
 * - Returns `sendToParent`
 */
// oxlint-disable-next-line typescript/no-unnecessary-type-parameters -- PAYLOAD is inferred from the `handler` argument at each call site (e.g. actions.submit's specific payload type). Hardcoding to `unknown` would make `handler` contravariantly incompatible with the specific-payload handlers actually passed in SandboxIframe.
export function useWindowMessage<PAYLOAD = unknown>(
	type: string,
	childOrigin: string,
	handler?: (payload: PAYLOAD) => void,
): { sendToParent: (message: OutgoingMessage) => void } {
	// Latest handler in a ref so the listener subscribes once and does not churn
	// as `handler`'s identity changes between renders. Updated in an effect (not
	// during render) to satisfy the react-hooks/refs rule.
	const handlerRef = useRef(handler);
	useEffect(() => {
		handlerRef.current = handler;
	});

	useEffect(() => {
		const win = getWindowSafe();

		const listener = (event: MessageEvent) => {
			if (event.origin !== childOrigin) return;
			const message = event.data as IncomingMessage<PAYLOAD> | undefined;
			if (message?.type !== type) return;
			handlerRef.current?.(message.payload);
		};

		win?.addEventListener('message', listener);

		return () => win?.removeEventListener('message', listener);
	}, [type, childOrigin]);

	const sendToParent = useCallback((message: OutgoingMessage) => {
		getWindowSafe()?.parent.postMessage(message, PARENT_ORIGIN);
	}, []);

	return { sendToParent };
}
